// app/frontend/src/pages/interface/components/newProduct/connect/drivers.ts
import fetchWithAuth from "@/api/secureFetch";
import {
    importTicketsStream, initiateAtlassianOAuth, listJiraProjects, verifyAtlassianDomain
} from "@/api/integrations/atlassian";
import {
    importWorkItemsStream, initiateAzureDevOpsOAuth, listAzureDevOpsProjects
} from "@/api/integrations/azureDevOps";
import { initiateGitHubOAuth } from "@/api/integrations/github";
import { initiateGitLabOAuth } from "@/api/integrations/gitlab";
import { initiateBitbucketOAuth } from "@/api/integrations/bitbucket";
import {
    fetchIntegrationCredentialStatus, fetchIntegrationLinks, listGitRepositories,
    type IntegrationCredentialStatusDto
} from "@/api/integrations/links";
import { openOAuthPopup, waitForOAuthPopup } from "@/utils/oauthPopup";
import type { SourceSelectionItem, SourceStaged } from "../state";
import type { ConnectSourceId } from "./sourceConfigs";

const AGENT_BASE_URL = import.meta.env.VITE_API_BASE_URL || "";

export interface ExistingAccount { account: string; host: string }
export interface ImportRunResult { staged: SourceStaged; importedCount: number }

export interface ConnectDriver {
    /** Git sources are selected here but imported by the durable draft job API. */
    readonly durableRepositoryImport?: boolean;
    checkExisting(token: string): Promise<ExistingAccount | null>;
    authorize(token: string, userId: number, configValue: string): Promise<void>;
    listTargets(token: string): Promise<SourceSelectionItem[]>;
    startImport(selection: SourceSelectionItem[], token: string,
                onProgress: (current: number, total: number) => void,
                signal: AbortSignal,
                options?: { durableRepositoryImport?: boolean }): Promise<ImportRunResult>;
}

function oauthMessageTypes(prefix: string): string[] {
    return ["success", "error", "retry", "cancel"].map(result => `${prefix}_oauth_${result}`);
}

function credentialVersion(status: IntegrationCredentialStatusDto): string {
    return status.updatedAt || status.lastSyncAt || status.createdAt || "";
}

function credentialWasUpdated(
    baseline: IntegrationCredentialStatusDto,
    current: IntegrationCredentialStatusDto
): boolean {
    if (!current.hasCredentials || current.isActive === false ||
        current.isConnected === false) return false;
    return !baseline.hasCredentials || credentialVersion(current) !== credentialVersion(baseline);
}

async function captureCredentialStatus(
    vendor: string,
    token: string
): Promise<IntegrationCredentialStatusDto | undefined> {
    try {
        return await fetchIntegrationCredentialStatus(vendor, token);
    } catch {
        // postMessage and manual-close handling remain available if the status
        // endpoint is temporarily unavailable.
        return undefined;
    }
}

async function popupFlow(
    authUrl: string | undefined,
    title: string,
    messagePrefix: string,
    completionCheck?: () => Promise<boolean>
): Promise<void> {
    if (!authUrl) throw new Error("No authorization URL received from server");
    const popup = openOAuthPopup(authUrl, title);
    if (popup) {
        await waitForOAuthPopup(popup, {
            authorizationUrl: authUrl,
            messageTypes: oauthMessageTypes(messagePrefix),
            completionCheck
        });
    }
}

type GitProviderKey = "github" | "gitlab" | "bitbucket" | "azure_repos";

/** Generic git-provider OAuth authorize call, used for providers that don't
 * have a dedicated typed client function (azure_repos). */
async function initiateGitProviderOAuth(
    provider: GitProviderKey,
    body: Record<string, unknown>,
    token: string
): Promise<{ authUrl?: string; authorizationUrl?: string; state?: string }> {
    const response = await fetchWithAuth(`${AGENT_BASE_URL}/api/v1/git/${provider}/oauth/authorize`, token, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body)
    });
    if (!response.ok) {
        throw new Error(`Failed to initiate OAuth: ${response.status}`);
    }
    return response.json();
}

interface GitRepositorySelectResponse {
    success: boolean;
    repositoryIds: number[];
}

async function selectGitRepositories(
    providerKey: GitProviderKey,
    selection: SourceSelectionItem[],
    token: string
): Promise<number[]> {
    const response = await fetchWithAuth(`${AGENT_BASE_URL}/api/v1/git/${providerKey}/repository/select`, token, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
            repositories: selection.map(s => ({
                name: s.name,
                full_name: s.name,
                clone_url: s.id,
                default_branch: s.key
            }))
        })
    });
    if (!response.ok) {
        throw new Error(`Failed to select repositories: ${response.status}`);
    }
    const data: GitRepositorySelectResponse = await response.json();
    return data.repositoryIds;
}

async function cloneGitRepository(repositoryId: number, token: string): Promise<void> {
    const response = await fetchWithAuth(`${AGENT_BASE_URL}/api/v1/git/repository/clone`, token, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ repositoryId })
    });
    if (!response.ok) throw new Error(`Failed to clone repository: ${response.status}`);
}

function makeJiraDriver(): ConnectDriver {
    let cloudId: string | undefined;
    let knownHost: string | undefined;

    return {
        async checkExisting(token) {
            const { vendors } = await fetchIntegrationLinks(token);
            const vendor = vendors.find(v => v.provider === "jira");
            if (!vendor || !vendor.connected) {
                // No connected credential — drop caches so a stale host or
                // cloud id from a disconnected account is never reused.
                cloudId = undefined;
                knownHost = undefined;
                return null;
            }
            if (vendor.cloudId) cloudId = vendor.cloudId;
            if (vendor.siteOrOrg) knownHost = vendor.siteOrOrg;
            return { account: vendor.siteOrOrg, host: vendor.siteOrOrg };
        },
        async authorize(token, userId, configValue) {
            // "Use this account" submits an empty configValue — fall back to
            // the account-on-file host learned from checkExisting.
            const domain = configValue || knownHost;
            if (!domain) throw new Error("Please enter your Atlassian domain");
            const baseline = await captureCredentialStatus("jira", token);
            const verify = await verifyAtlassianDomain(domain, token);
            if (verify.cloudId) cloudId = verify.cloudId;
            const oauth = await initiateAtlassianOAuth({ userId, domain, cloudId }, token);
            await popupFlow(
                oauth.authUrl ?? oauth.authorizationUrl,
                "Connect Jira",
                "atlassian",
                baseline ? async () => credentialWasUpdated(
                    baseline, await fetchIntegrationCredentialStatus("jira", token)
                ) : undefined
            );
        },
        async listTargets(token) {
            if (!cloudId) throw new Error("No Jira cloud ID available");
            const { projects } = await listJiraProjects(cloudId, token);
            return projects.map(p => ({ id: p.id, key: p.key, name: p.name }));
        },
        async startImport(selection, token, onProgress, signal) {
            if (!cloudId) throw new Error("No Jira cloud ID available");
            const sel = selection[0];
            const result = await importTicketsStream(
                { cloudId, projectId: sel.id, projectName: sel.name, projectKey: sel.key! },
                token,
                p => onProgress(p.current, p.total),
                signal
            );
            return {
                staged: { processJobId: result.process_job_id, importedCount: result.imported },
                importedCount: result.imported
            };
        }
    };
}

function parseAzureOrganization(configValue: string): string {
    const match = configValue.match(/dev\.azure\.com\/([^/]+)/);
    return match ? match[1] : configValue;
}

function makeAdoDriver(): ConnectDriver {
    let organization: string | undefined;

    return {
        async checkExisting(token) {
            const { vendors } = await fetchIntegrationLinks(token);
            const vendor = vendors.find(v => v.provider === "azure-devops");
            if (!vendor || !vendor.connected) return null;
            if (vendor.organization) organization = vendor.organization;
            return { account: vendor.siteOrOrg, host: vendor.siteOrOrg };
        },
        async authorize(token, userId, configValue) {
            // Keep the cached organization when "Use this account" submits an
            // empty configValue; only re-parse when the user typed one.
            if (configValue) organization = parseAzureOrganization(configValue);
            if (!organization) throw new Error("Please enter your Azure DevOps organization URL");
            const baseline = await captureCredentialStatus("azure", token);
            const oauth = await initiateAzureDevOpsOAuth({ userId, organization }, token);
            await popupFlow(
                oauth.authUrl ?? oauth.authorizationUrl,
                "Connect Azure DevOps",
                "azure_devops",
                baseline ? async () => credentialWasUpdated(
                    baseline, await fetchIntegrationCredentialStatus("azure", token)
                ) : undefined
            );
        },
        async listTargets(token) {
            if (!organization) throw new Error("No Azure DevOps organization available");
            const { projects } = await listAzureDevOpsProjects(organization, token);
            return projects.map(p => ({ id: p.id, name: p.name }));
        },
        async startImport(selection, token, onProgress, signal) {
            if (!organization) throw new Error("No Azure DevOps organization available");
            const sel = selection[0];
            const result = await importWorkItemsStream(
                { organization, projectId: sel.id, projectName: sel.name },
                token,
                p => onProgress(p.current, p.total),
                signal
            );
            return {
                staged: { processJobId: result.process_job_id, importedCount: result.imported },
                importedCount: result.imported
            };
        }
    };
}

function makeGitDriver(providerKey: GitProviderKey): ConnectDriver {
    return {
        durableRepositoryImport: true,
        async checkExisting(token) {
            const { repositories } = await listGitRepositories(providerKey, token);
            return repositories.length > 0 ? { account: providerKey, host: providerKey } : null;
        },
        async authorize(token, userId, configValue) {
            const baseline = await captureCredentialStatus(providerKey, token);
            let oauth: { authUrl?: string; authorizationUrl?: string };
            switch (providerKey) {
                case "github":
                    oauth = await initiateGitHubOAuth({ userId }, token);
                    break;
                case "gitlab":
                    oauth = await initiateGitLabOAuth({ userId, gitlabUrl: configValue || undefined }, token);
                    break;
                case "bitbucket":
                    oauth = await initiateBitbucketOAuth({ userId, workspace: configValue || undefined }, token);
                    break;
                case "azure_repos":
                    oauth = await initiateGitProviderOAuth("azure_repos", {
                        userId,
                        organization: configValue
                            ? parseAzureOrganization(configValue)
                            : undefined
                    }, token);
                    break;
            }
            await popupFlow(
                oauth.authUrl ?? oauth.authorizationUrl,
                `Connect ${providerKey}`,
                providerKey,
                baseline ? async () => credentialWasUpdated(
                    baseline, await fetchIntegrationCredentialStatus(providerKey, token)
                ) : undefined
            );
        },
        async listTargets(token) {
            const { repositories } = await listGitRepositories(providerKey, token);
            return repositories.map(r => ({
                id: r.clone_url || r.url || "",
                key: r.default_branch || "main",
                name: r.full_name || r.name
            }));
        },
        async startImport(selection, token, onProgress, signal, options) {
            const repoIds = await selectGitRepositories(providerKey, selection, token);
            if (signal.aborted) return { staged: { repoIds, importedCount: 0 }, importedCount: 0 };
            if (options?.durableRepositoryImport) {
                // Selection is intentionally the only synchronous operation.
                // The creation form persists every id before scheduling work.
                onProgress(0, repoIds.length);
                return { staged: { repoIds, importedCount: 0 }, importedCount: 0 };
            }
            // Product Refresh and any other legacy consumer keep their current
            // contract until they are deliberately migrated.
            for (let index = 0; index < repoIds.length; index += 1) {
                if (signal.aborted) break;
                await cloneGitRepository(repoIds[index], token);
                onProgress(index + 1, repoIds.length);
            }
            return { staged: { repoIds, importedCount: repoIds.length }, importedCount: repoIds.length };
        }
    };
}

const drivers: Record<ConnectSourceId, ConnectDriver> = {
    jira: makeJiraDriver(),
    azure: makeAdoDriver(),
    github: makeGitDriver("github"),
    gitlab: makeGitDriver("gitlab"),
    bitbucket: makeGitDriver("bitbucket"),
    azuregit: makeGitDriver("azure_repos")
};

export function getDriver(source: ConnectSourceId): ConnectDriver {
    return drivers[source];
}
